import React from 'react';
import { Alert, Box, CircularProgress, Paper, TextField, Typography } from '@mui/material';

import { AppPagination } from '@/__core/components';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { getIssues } from '@/modules/inventory/issues/redux/IssueCRUD';
import { getReceipts } from '@/modules/inventory/receipts/redux/ReceiptCRUD';

type HistoryDirection = 'receipt' | 'issue';

interface ItemInventoryHistoryProps {
  itemId?: string;
  companyId?: string;
  direction: HistoryDirection;
  active: boolean;
}

interface ItemInventoryHistoryRow {
  id: string;
  documentNo: string;
  occurredAt: string;
  warehouseName: string;
  reference: string;
  quantity: number;
  uomName: string;
  unitCost: number;
  lineAmount: number;
  status: string;
}

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatQuantity = (value: number) => value.toLocaleString('vi-VN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatMoney = (value: number) => value.toLocaleString('vi-VN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const resolveStatus = (
  status: unknown,
  statusCode?: string,
  statusDisplay?: string,
) => {
  if (statusDisplay) return statusDisplay;
  if (status && typeof status === 'object') {
    const relation = status as { name?: string; code?: string };
    return relation.name || relation.code || statusCode || '—';
  }
  return String(status || statusCode || '—');
};

export default function ItemInventoryHistory({
  itemId,
  companyId,
  direction,
  active,
}: ItemInventoryHistoryProps) {
  const [rows, setRows] = React.useState<ItemInventoryHistoryRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [count, setCount] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [search, setSearch] = React.useState('');

  const filteredRows = React.useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) =>
      row.documentNo.toLowerCase().includes(q) ||
      row.warehouseName.toLowerCase().includes(q) ||
      row.reference.toLowerCase().includes(q) ||
      row.status.toLowerCase().includes(q)
    );
  }, [rows, search]);

  React.useEffect(() => {
    setPage(1);
    setRows([]);
    setCount(0);
    setTotalPages(1);
    setError('');
    setSearch('');
  }, [itemId, direction]);

  React.useEffect(() => {
    if (!active || !itemId) return;

    let mounted = true;

    const loadHistory = async () => {
      setLoading(true);
      setError('');

      try {
        if (direction === 'receipt') {
          const response = await getReceipts({
            companyId,
            itemId,
            page,
            pageSize: DEFAULT_PAGE_SIZE,
          });
          if (!mounted) return;

          const nextRows = response.results.flatMap((receipt) =>
            (receipt.lines || [])
              .filter((line) => line.item_id === itemId)
              .map((line) => ({
                id: `${receipt.id}-${line.id}`,
                documentNo: receipt.receipt_no,
                occurredAt: receipt.received_at,
                warehouseName: receipt.warehouse_name,
                reference: receipt.purchase_no || receipt.supplier_name || '—',
                quantity: Number(line.received_qty) || 0,
                uomName: line.uom_name,
                unitCost: Number(line.unit_cost) || 0,
                lineAmount: Number(line.line_amount) || ((Number(line.received_qty) || 0) * (Number(line.unit_cost) || 0)),
                status: resolveStatus(receipt.status, receipt.status_code, receipt.status_display),
              })),
          );

          setRows(nextRows);
          setCount(response.count);
          setTotalPages(response.totalPages || 1);
          return;
        }

        const response = await getIssues({
          companyId,
          itemId,
          page,
          pageSize: DEFAULT_PAGE_SIZE,
        });
        if (!mounted) return;

        const nextRows = response.results.flatMap((issue) =>
          (issue.lines || [])
            .filter((line) => line.item_id === itemId)
            .map((line) => ({
              id: `${issue.id}-${line.id}`,
              documentNo: issue.code,
              occurredAt: issue.issued_at || issue.created_at || '',
              warehouseName: issue.store_name,
              reference: issue.order_no || issue.material_request_no || '—',
              quantity: Number(line.issued_qty) || 0,
              uomName: line.uom_name,
              unitCost: Number(line.unit_cost) || 0,
              lineAmount: Number(line.line_amount) || ((Number(line.issued_qty) || 0) * (Number(line.unit_cost) || 0)),
              status: resolveStatus(issue.status, issue.status_code, issue.status_display),
            })),
        );

        setRows(nextRows);
        setCount(response.count);
        setTotalPages(response.total_pages || 1);
      } catch (_error) {
        if (!mounted) return;
        setRows([]);
        setCount(0);
        setTotalPages(1);
        setError(direction === 'receipt'
          ? 'Không thể tải lịch sử nhập kho của vật tư.'
          : 'Không thể tải lịch sử xuất kho của vật tư.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void loadHistory();

    return () => {
      mounted = false;
    };
  }, [active, companyId, direction, itemId, page]);

  if (!active) {
    return null;
  }

  if (!itemId) {
    return (
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2 }}>
        <Alert severity='info'>Vui lòng lưu vật tư trước khi xem lịch sử kho.</Alert>
      </Paper>
    );
  }

  const columns = [
    {
      id: 'documentNo',
      label: direction === 'receipt' ? 'Phiếu nhập' : 'Phiếu xuất',
      width: '160px',
      render: (_value: unknown, row: ItemInventoryHistoryRow) => (
        <Typography sx={{ fontWeight: 600 }}>{row.documentNo || '—'}</Typography>
      ),
    },
    {
      id: 'occurredAt',
      label: direction === 'receipt' ? 'Ngày nhập' : 'Ngày xuất',
      width: '170px',
      render: (_value: unknown, row: ItemInventoryHistoryRow) => formatDateTime(row.occurredAt),
    },
    {
      id: 'warehouseName',
      label: direction === 'receipt' ? 'Kho nhập' : 'Kho xuất',
      width: '180px',
    },
    {
      id: 'reference',
      label: direction === 'receipt' ? 'PO / Nhà cung cấp' : 'Đơn hàng / Yêu cầu',
      width: '220px',
    },
    {
      id: 'quantity',
      label: 'Số lượng',
      width: '120px',
      align: 'right' as const,
      render: (_value: unknown, row: ItemInventoryHistoryRow) => formatQuantity(row.quantity),
    },
    { id: 'uomName', label: 'ĐVT', width: '100px' },
    {
      id: 'unitCost',
      label: 'Đơn giá',
      width: '140px',
      align: 'right' as const,
      render: (_value: unknown, row: ItemInventoryHistoryRow) => formatMoney(row.unitCost),
    },
    {
      id: 'lineAmount',
      label: 'Thành tiền',
      width: '150px',
      align: 'right' as const,
      render: (_value: unknown, row: ItemInventoryHistoryRow) => formatMoney(row.lineAmount),
    },
    { id: 'status', label: 'Trạng thái', width: '150px' },
  ];

  return (
    <>
      {error ? <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert> : null}

      <TextField
        size='small'
        fullWidth
        placeholder={direction === 'receipt' ? 'Tìm kiếm phiếu nhập, kho, tham chiếu...' : 'Tìm kiếm phiếu xuất, kho, tham chiếu...'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={30} />
        </Box>
      ) : filteredRows.length > 0 ? (
        <>
          <DataTableModule
            data={filteredRows}
            columns={columns}
            leftFilterWidth={0}
            onRowSelect={() => undefined}
            onSelectAll={() => undefined}
            getRowId={(row) => (row as ItemInventoryHistoryRow).id}
            isRowSelected={() => false}
            areAllRowsSelected={false}
            areSomeRowsSelected={false}
            showCheckbox={false}
          />
          <AppPagination
            count={count}
            page={page}
            pageSize={DEFAULT_PAGE_SIZE}
            totalPages={totalPages}
            itemLabel={direction === 'receipt' ? 'phiếu nhập' : 'phiếu xuất'}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
          />
        </>
      ) : search ? (
        <Box sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color='text.secondary'>Không tìm thấy kết quả phù hợp.</Typography>
        </Box>
      ) : (
        <Box sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color='text.secondary'>
            {direction === 'receipt'
              ? 'Vật tư chưa có lịch sử nhập kho.'
              : 'Vật tư chưa có lịch sử xuất kho.'}
          </Typography>
        </Box>
      )}
    </>
  );
}
